import { useState, useCallback, useRef } from 'react';
import { analyticAccountingApi } from '../services/api/endpoints/analyticAccounting';
import { useBusinessUnitStore } from '../stores/businessUnitStore';
import type {
  AnalyticAccount,
  CreateAnalyticAccountPayload,
  UpdateAnalyticAccountPayload,
} from '../types/analytic-accounting';

export function useAnalyticAccounts() {
  const [accounts, setAccounts] = useState<AnalyticAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadRequestIdRef = useRef(0);
  const { effectiveFilter } = useBusinessUnitStore();
  const buId = effectiveFilter.businessUnitId;
  const buType = effectiveFilter.businessUnitType;

  const loadAccounts = useCallback(async (params?: { type?: string; isActive?: boolean; search?: string }) => {
    const requestId = ++loadRequestIdRef.current;
    try {
      setLoading(true);
      setError(null);

      // Note métier : les comptes analytiques eux-mêmes sont
      // FY-agnostiques (= permanents, comme le plan comptable). On
      // filtre seulement par unité commerciale pour les coopératives /
      // entreprises multi-sites où chaque section a son propre périmètre.
      const response = await analyticAccountingApi.getAccounts({
        ...params,
        businessUnitId: buId,
        businessUnitType: buType,
      } as any);

      if (requestId !== loadRequestIdRef.current) return;

      if (response && 'data' in response) {
        const data = Array.isArray(response.data) ? response.data : (response.data as any)?.data || [];
        setAccounts(data);
      }
    } catch (err: any) {
      if (requestId !== loadRequestIdRef.current) return;
      setError(err.message || 'Erreur lors du chargement des comptes analytiques');
    } finally {
      if (requestId === loadRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [buId, buType]);

  const createAccount = useCallback(async (data: CreateAnalyticAccountPayload): Promise<AnalyticAccount | null> => {
    try {
      setError(null);
      const response = await analyticAccountingApi.createAccount(data);
      if (response && 'data' in response) {
        const newAccount = (response.data as any)?.data || response.data;
        setAccounts(prev => [...prev, newAccount as AnalyticAccount]);
        return newAccount as AnalyticAccount;
      }
      return null;
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la création');
      return null;
    }
  }, []);

  const updateAccount = useCallback(async (id: string, data: UpdateAnalyticAccountPayload): Promise<AnalyticAccount | null> => {
    try {
      setError(null);
      const response = await analyticAccountingApi.updateAccount(id, data);
      if (response && 'data' in response) {
        const updated = (response.data as any)?.data || response.data;
        setAccounts(prev => prev.map(a => a.id === id ? updated as AnalyticAccount : a));
        return updated as AnalyticAccount;
      }
      return null;
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la mise à jour');
      return null;
    }
  }, []);

  const deleteAccount = useCallback(async (id: string): Promise<boolean> => {
    try {
      setError(null);
      await analyticAccountingApi.deleteAccount(id);
      setAccounts(prev => prev.filter(a => a.id !== id));
      return true;
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la suppression');
      return false;
    }
  }, []);

  // Build tree structure for hierarchical display
  const accountTree = accounts.filter(a => !a.parentId).map(parent => ({
    ...parent,
    children: accounts.filter(child => child.parentId === parent.id),
  }));

  const activeAccounts = accounts.filter(a => a.isActive);

  return {
    accounts,
    accountTree,
    activeAccounts,
    loading,
    error,
    loadAccounts,
    createAccount,
    updateAccount,
    deleteAccount,
  };
}
